import { motion, useInView } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { Users, Globe, Award, TrendingUp } from 'lucide-react';
import Eyebrow from './Eyebrow';
import { api } from '../api';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

const values = [
  { icon: Users,     title: 'Client-First',     desc: 'Every decision starts with your users and ends with your business outcomes.',      gradient: 'linear-gradient(135deg, #5E0ED7 0%, #7c3aed 100%)', light: '#f5f3ff' },
  { icon: Globe,     title: 'Global Talent',     desc: 'Senior engineers and designers across multiple time zones, built for velocity.',    gradient: 'linear-gradient(135deg, #0891b2 0%, #0284c7 100%)', light: '#e0f2fe' },
  { icon: Award,     title: 'Quality Standard',  desc: 'Enterprise-grade code review, testing, and security practices on every project.',   gradient: 'linear-gradient(135deg, #d97706 0%, #ea580c 100%)', light: '#fef3c7' },
  { icon: TrendingUp,title: 'Outcome-Driven',    desc: "We measure success by impact on your metrics — not delivery of deliverables.",     gradient: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)', light: '#d1fae5' },
];

const DEFAULT_STATS = [
  { target: 8,   suffix: '+', label: 'Years Operating' },
  { target: 120, suffix: '+', label: 'Team Members'    },
  { target: 15,  suffix: '+', label: 'Industries'      },
];

function AnimatedCounter({ target, suffix, inView }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1400;
    function tick(now) {
      const p = Math.min((now - start) / dur, 1);
      setCount(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) requestAnimationFrame(tick);
    }
    const raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return <span className="tabular-nums">{count}{suffix}</span>;
}

function ValueCard({ v, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const Icon = v.icon;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.07, duration: 0.5, ease }}
      whileHover={{ y: -4, scale: 1.01 }}
      className="cursor-default h-full"
    >
      <div className="rounded-2xl overflow-hidden h-full" style={{ boxShadow: '0 4px 16px -4px rgba(0,0,0,0.10)' }}>
        <div className="h-14 flex items-center px-4" style={{ background: v.gradient }}>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.22)' }}>
            <Icon size={15} color="#fff" />
          </div>
        </div>
        <div className="bg-white p-5">
          <h3 className="text-sm font-bold mb-1.5" style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{v.title}</h3>
          <p className="text-xs leading-relaxed" style={{ color: '#6B6B72' }}>{v.desc}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: '-60px' });
  const [statsData, setStatsData] = useState(DEFAULT_STATS);

  useEffect(() => {
    api.getConfig('about').then(d => {
      if (d?.years) setStatsData([
        { target: d.years,      suffix: '+', label: 'Years Operating' },
        { target: d.team,       suffix: '+', label: 'Team Members'    },
        { target: d.industries, suffix: '+', label: 'Industries'      },
      ]);
    }).catch(() => {});
  }, []);

  return (
    <section id="story" className="px-5 sm:px-8 md:px-12 lg:px-20 flex flex-col justify-center py-24 section-gray" style={{ minHeight: '100dvh' }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div ref={ref}>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, ease }}>
              <Eyebrow>About Us</Eyebrow>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.08, ease }}
              className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.08] mb-6"
              style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.025em' }}
            >
              We Build What Others Only Consult On
            </motion.h2>

            {[
              { t: 'Syenovix is a technology consulting and digital product studio. We partner with ambitious organizations — from Series A startups to Fortune 500 enterprises — to design, build, and scale technology that creates real competitive advantage.', d: 0.16 },
              { t: 'Unlike traditional consultancies that produce roadmaps and walk away, we stay embedded through implementation. Our teams have shipped 200+ digital products and deployed AI solutions across 15+ industries.', d: 0.22 },
              { t: 'We believe great technology is invisible — it just works, elegantly and reliably, accelerating every part of your business.', d: 0.28 },
            ].map(({ t, d }, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 16 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: d, ease }}
                className="text-base leading-relaxed mb-4"
                style={{ color: '#6B6B72' }}
              >
                {t}
              </motion.p>
            ))}

            {/* Animated counters */}
            <motion.div
              ref={statsRef}
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.38, ease }}
              className="flex gap-8 mt-10 pt-8"
              style={{ borderTop: '1px solid rgba(24,24,27,0.08)' }}
            >
              {statsData.map((s) => (
                <div key={s.label}>
                  <div
                    className="text-3xl font-bold tracking-tight"
                    style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    <AnimatedCounter target={s.target} suffix={s.suffix} inView={statsInView} />
                  </div>
                  <div className="text-[11px] font-medium mt-1 tracking-wider uppercase" style={{ color: '#A0A0A7' }}>{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-stretch">
            {values.map((v, i) => <ValueCard key={v.title} v={v} index={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
